// RoastLog
import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { getTempConverters, globalState, type CurrentTempReading } from '$lib/State.svelte';
import type { Nullable } from '$lib/types/RoastLog';
import type { ConnectOptions, Device } from '..';
import { getDeviceConfig } from '../registry';

/**
 * BaseDevice provides common functionality for all device implementations.
 * Each specific device only needs to implement the abstract methods.
 */
export abstract class BaseDevice implements Device {
  abstract type: DeviceType;
  protected timer: Nullable<
    ReturnType<typeof setInterval> | Promise<ReturnType<typeof setInterval> | null>
  > = null;
  protected localReadings: CurrentTempReading = [...globalState.rawTemps];
  protected tcSettings: ConnectOptions['tcSettings'] | null = null;
  protected pollingInterval: number = 1000; // Default to 1 second

  /**
   * Connect to the device and start polling for data
   */
  async connect(options: ConnectOptions): Promise<boolean> {
    if (!globalState.deviceConnected) {
      globalState.deviceConnecting = true;

      this.tcSettings = options.tcSettings;
      this.timer = await this.startTimer(options);

      globalState.deviceConnecting = false;

      return this.timer !== null;
    } else {
      await this.stopTimer();
      return false;
    }
  }

  /**
   * Disconnect from the device and stop polling
   */
  async disconnect(): Promise<void> {
    await this.stopTimer();
    this.onDisconnect();
    globalState.deviceConnected = false;
  }

  /**
   * Start the timer to poll for data
   */
  protected async startTimer(
    options: ConnectOptions
  ): Promise<ReturnType<typeof setInterval> | null> {
    const connectionResult = await this.onConnect(options);

    if (connectionResult === false) {
      logger.warn('Failed to connect to device');
      globalState.deviceConnected = false;
      return null;
    }

    logger.info('Device timer starting', { options });
    globalState.deviceConnected = true;

    // Initial update with current readings
    globalState.setTemps(this.localReadings, null, Date.now());

    // Start polling
    return setInterval(async () => {
      try {
        // Get readings from the device
        await this.updateReadings();

        // Apply offsets if needed
        const adjustedReadings = this.applyOffsets(this.localReadings);

        // Update global state
        globalState.setTemps(adjustedReadings, await this.getPowerLevel(), Date.now());
      } catch (error) {
        logger.error(`Error in device polling: ${error}`);
        this.handleError(error);
      }
    }, this.pollingInterval);
  }

  /**
   * Stop the timer
   */
  protected async stopTimer(): Promise<void> {
    if (this.timer) {
      if (this.timer instanceof Promise) {
        const resolvedTimer = await this.timer;
        if (resolvedTimer) {
          clearInterval(resolvedTimer);
        }
        this.timer = Promise.resolve(null);
      } else {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
    globalState.deviceConnected = false;
  }

  /**
   * Apply temperature offsets based on global settings
   */
  protected applyOffsets(readings: CurrentTempReading): CurrentTempReading {
    if (!this.tcSettings) return readings;

    const converters = getTempConverters();
    return readings.map((reading, index) => {
      if (reading === null) return null;

      // Apply offset from tcSettings if available
      const offset = this.tcSettings?.[index]?.offset || 0;
      return reading + converters.tempToCelsiusConverter(offset);
    }) as CurrentTempReading;
  }

  /**
   * Handle errors during polling
   */
  protected handleError(error: unknown): void {
    logger.error(`Device error: ${error}`);
    // Default implementation stops the timer on error
    this.stopTimer();
  }

  /**
   * Get the current power level (if supported by the device)
   * Default implementation returns null (no power level)
   */
  protected async getPowerLevel(): Promise<number | null> {
    return null;
  }

  /**
   * Get the Modbus port for this device type
   */
  protected getModbusPort(): number {
    return getDeviceConfig(this.type).modbusPort || 502;
  }

  /**
   * Called when the device is connected
   * Return false to indicate connection failure
   */
  protected abstract onConnect(options: ConnectOptions): Promise<boolean>;

  /**
   * Called when the device is disconnected
   */
  protected abstract onDisconnect(): void;

  /**
   * Update the local readings from the device
   */
  protected abstract updateReadings(): void | Promise<void>;
}
